// src/store/challenge/challengeSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchChallenges,
  fetchChallengeById,
  updateChallengeProgress,
  createChallenge
} from './challengeThunk';

const initialState = {
  challenges: [],
  currentChallenge: null,
  
  // Loading states
  loading: false,
  createLoading: false,
  updateLoading: false,
  
  // Error states
  error: null,
  createError: null,
  updateError: null,
  
  // Success messages
  successMessage: null,
  
  // Pagination
  total: 0
};

const challengeSlice = createSlice({
  name: 'challenge',
  initialState,
  reducers: {
    setCurrentChallenge: (state, action) => {
      state.currentChallenge = action.payload;
    },
    
    clearCurrentChallenge: (state) => {
      state.currentChallenge = null;
    },
    
    clearError: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
    },
    
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
    // Local progress update (optimistic update)
    updateLocalProgress: (state, action) => {
      const { challengeId, progress } = action.payload;
      const challenge = state.challenges.find(c => c.id === challengeId);
      if (challenge) {
        challenge.progress = progress;
        if (progress >= 100) {
          challenge.status = 'completed';
        }
      }
    }
  },
  
  extraReducers: (builder) => {
    // FETCH ALL CHALLENGES
    builder
      .addCase(fetchChallenges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChallenges.fulfilled, (state, action) => {
        state.loading = false;
        state.challenges = action.payload?.challenges || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchChallenges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch challenges';
        state.challenges = [];
        state.total = 0;
      })
    
    // FETCH CHALLENGE BY ID
      .addCase(fetchChallengeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChallengeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChallenge = action.payload?.challenge || null;
      })
      .addCase(fetchChallengeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch challenge';
        state.currentChallenge = null;
      })
    
    // UPDATE CHALLENGE PROGRESS
      .addCase(updateChallengeProgress.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.successMessage = null;
      })
      .addCase(updateChallengeProgress.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.successMessage = action.payload?.message || 'Progress updated';
        
        // Update in list
        if (action.payload?.progress) {
          const challengeId = action.payload.progress.challenge_id;
          const challenge = state.challenges.find(c => c.id === challengeId);
          
          if (challenge) {
            challenge.progress = action.payload.progress.progress;
            challenge.status = action.payload.progress.status;
          }
          
          // Update current challenge if it matches
          if (state.currentChallenge && state.currentChallenge.id === challengeId) {
            state.currentChallenge.progress = action.payload.progress.progress;
            state.currentChallenge.status = action.payload.progress.status;
          }
        }
      })
      .addCase(updateChallengeProgress.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || 'Failed to update progress';
      })
    
    // CREATE CHALLENGE
      .addCase(createChallenge.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.successMessage = null;
      })
      .addCase(createChallenge.fulfilled, (state, action) => {
        state.createLoading = false;
        state.successMessage = action.payload.message || 'Challenge created successfully';
        
        // Add to challenges list
        if (action.payload.challenge) {
          state.challenges.unshift({
            ...action.payload.challenge,
            progress: 0,
            status: 'not_started'
          });
          state.total += 1;
        }
      })
      .addCase(createChallenge.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || 'Failed to create challenge';
      });
  }
});

// Actions
export const {
  setCurrentChallenge,
  clearCurrentChallenge,
  clearError,
  clearSuccessMessage,
  updateLocalProgress
} = challengeSlice.actions;

// Selectors
export const selectChallenges = (state) => state.challenge.challenges;
export const selectCurrentChallenge = (state) => state.challenge.currentChallenge;
export const selectChallengeLoading = (state) => state.challenge.loading;
export const selectCreateLoading = (state) => state.challenge.createLoading;
export const selectUpdateLoading = (state) => state.challenge.updateLoading;
export const selectChallengeError = (state) => state.challenge.error;
export const selectCreateError = (state) => state.challenge.createError;
export const selectUpdateError = (state) => state.challenge.updateError;
export const selectSuccessMessage = (state) => state.challenge.successMessage;
export const selectTotal = (state) => state.challenge.total;

export default challengeSlice.reducer;